"use client";

import { FC } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import ArrowRight from "@/assets/icons/arrow-right.svg?react";
import ArrowLeft from "@/assets/icons/arrow-left.svg?react";

interface TypeDatePickerProps {
  selected: Date | null;
  minDate?: Date;
  holidays?: Date[];
  onChange: (date: Date | null) => void;
  onMonthChange?: (date: Date) => void;
}

const DatePicker: FC<TypeDatePickerProps> = ({
  selected,
  minDate,
  holidays,
  onChange,
  onMonthChange,
}) => {
  // This function filters out dates from other months
  const filterDates = (date: Date) => {
    // Get the currently displayed month in the calendar
    const currentlyDisplayedMonth = selected ? selected.getMonth() : new Date().getMonth();
    const currentlyDisplayedYear = selected ? selected.getFullYear() : new Date().getFullYear();

    // Check if date is from the currently displayed month and year
    const isCurrentMonth =
      date.getMonth() === currentlyDisplayedMonth && date.getFullYear() === currentlyDisplayedYear;

    return isCurrentMonth;
  };

  return (
    <div className="custom-date-picker h-full w-full">
      <style jsx global>{`
        .custom-date-picker {
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
        }

        .custom-date-picker .react-datepicker {
          border: none;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .custom-date-picker .react-datepicker__header {
          background-color: white;
          border-bottom: none;
          padding-top: 10px;
        }

        .custom-date-picker .react-datepicker__navigation {
          top: 15px;
        }

        .custom-date-picker .react-datepicker__month-container {
          width: 100%;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .custom-date-picker .react-datepicker__month {
          margin: 0;
          padding: 0 10px;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .custom-date-picker .react-datepicker__day-names {
          display: flex;
          justify-content: space-between;
          margin-bottom: 15px;
          padding: 0 15px;
          width: 100%;
        }

        .custom-date-picker .react-datepicker__day-name {
          flex: 1;
          text-align: center;
          margin: 0 5px;
          font-weight: normal;
          color: #000;
        }

        .custom-date-picker .react-datepicker__week {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
          width: 100%;
        }

        .custom-date-picker .react-datepicker__day {
          flex: 1;
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 10px;
          border-radius: 0;
          border: 1px solid #e6e6e6;
          font-weight: normal;
          min-width: 0;
          line-height: normal;
          height: auto;
          position: relative;
          overflow: hidden;
        }

        .custom-date-picker .react-datepicker__day:hover {
          border-radius: 0;
          background-color: #f0f0f0;
        }

        .custom-date-picker .react-datepicker__day--selected:hover {
          background-color: #000;
          color: white;
          border-radius: 0;
        }

        .custom-date-picker .react-datepicker__day--selected {
          background-color: #000;
          color: white;
          border-radius: 0;
        }

        .custom-date-picker .react-datepicker__day--keyboard-selected {
          background-color: #000;
          color: white;
          border-radius: 0;
        }

        .custom-date-picker .react-datepicker__day--disabled {
          color: #ccc;
        }

        .custom-date-picker .react-datepicker__current-month {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 15px;
          text-align: center;
        }

        /* Hide the dates from other months */
        .custom-date-picker .react-datepicker__day--outside-month {
          visibility: hidden;
          pointer-events: none;
        }

        .diagonal-line {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            to bottom right,
            transparent calc(50% - 1px),
            #e6e6e6,
            transparent calc(50% + 1px)
          );
        }
      `}</style>

      <ReactDatePicker
        selected={selected}
        minDate={minDate}
        excludeDates={holidays}
        onChange={onChange}
        onMonthChange={onMonthChange}
        calendarStartDay={1}
        inline
        useWeekdaysShort

        filterDate={filterDates}
        showMonthYearPicker={false}
        showFullMonthYearPicker={false}
        showFourColumnMonthYearPicker={false}
        showPreviousMonths={false}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="flex w-full items-center justify-center gap-5 px-6 py-2">
            <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} className="p-1">
              <ArrowLeft />
            </button>
            <h2 className="text-xl font-bold">{format(date, "MMMM, yyyy")}</h2>
            <button onClick={increaseMonth} disabled={nextMonthButtonDisabled} className="p-1">
              <ArrowRight />
            </button>
          </div>
        )}
      />
    </div>
  );
};

export default DatePicker;
